import 'swiper/scss';
import { FreeMode } from 'swiper';
import { useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSearchParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Entry } from 'contentful';

import { client } from '../../helpers/contentful';
import { Spinner, ToolCard, SkeletonPage, PaywallModal, SearchBar, SeoIntro } from '../../components';
import { SignIn } from '../../components/SignIn';
import { IBrushFields } from '../../@types/generated/contentful';
import { UserContext } from '../../context/userContext';
import { analytics } from '../../helpers/analytics';
import { ArrowRight } from '../../Icons';
import paywallBg from '../../assets/images/paywall-bg.png';

const homeBannerVideo =
  'https://firebasestorage.googleapis.com/v0/b/tools-4372b.appspot.com/o/Paywalls%2Fhome_banner%2FIMG_0117.MOV?alt=media&token=bde07473-f30c-4c02-9e9c-851025d1d7ec';

const LOGGED_OUT_VISIBLE_BRUSHES = 12;

const Home = () => {
  const [brushes, setBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [newBrushes, setNewBrushes] = useState<Entry<IBrushFields>[]>([]);
  const [total, setTotal] = useState(0);
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const { user } = useContext(UserContext);
  const { email = '', hasPremium = false, id: userId = null, plan = null } = user || {};
  const isLoggedIn = Boolean(email);
  const isBrushFeedLocked = !isLoggedIn && total > LOGGED_OUT_VISIBLE_BRUSHES;
  const searchTerm = searchParams.get('search')?.trim() || '';
  const isSearchLanding = Boolean(searchTerm);

  const getBrushQuery = (skip: number) => {
    const query: Record<string, string | number | boolean> = {
      skip,
      limit: 12,
      'fields.categoryName': 'Brushes',
      content_type: 'brush',
    };

    if (searchTerm) {
      query.query = searchTerm;
    }

    return query;
  };

  const trackBrushesLoaded = (loadedCount: number, totalCount: number, skip: number) => {
    analytics.track("Content Collection Loaded", {
      category: "Brushes",
      source: searchTerm ? "home_search" : "home_all_brushes",
      query: searchTerm || undefined,
      loaded_count: loadedCount,
      total_count: totalCount,
      skip,
      is_logged_in: isLoggedIn,
    });
  };

  useEffect(() => {
    const getNewBrushes = async () => {
      const response = await client
        .getEntries<IBrushFields>({
          'fields.categoryName': 'Brushes',
          content_type: 'brush',
          'fields.featuredText[exists]': true,
        })
        .then((r) => r.items);

      return response;
    };

    getNewBrushes().then((r) => setNewBrushes(r));
  }, []);

  const getBrushes = async () => {
    const response = await client
      .getEntries<IBrushFields>(getBrushQuery(brushes.length))
      .then((r) => r);

    if (total === 0) {
      setTotal(response.total);
    }

    setBrushes((state) => [...state, ...response.items]);
    trackBrushesLoaded(response.items.length, response.total, brushes.length);
    return response.items;
  };

  useEffect(() => {
    let isCurrent = true;

    setLoading(true);
    setBrushes([]);
    setTotal(0);

    client
      .getEntries<IBrushFields>(getBrushQuery(0))
      .then((response) => {
        if (!isCurrent) {
          return;
        }

        setTotal(response.total);
        setBrushes(response.items);
        trackBrushesLoaded(response.items.length, response.total, 0);
      })
      .finally(() => {
        if (isCurrent) {
          setLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isLoggedIn]);

  const handleSearch = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set('search', value);
      analytics.track("Search Performed", {
        query: value,
        category: "Brushes",
        source: "home",
      });
    } else {
      nextParams.delete('search');
    }

    nextParams.delete('tool');
    setSearchParams(nextParams);
  };

  const handleBannerClick = () => {
    analytics.track("Home Banner Clicked", {
      has_premium: hasPremium,
      is_logged_in: isLoggedIn,
      source: "home_banner",
    });

    if (hasPremium) {
      return;
    }

    analytics.track('Paywall', { premium: hasPremium, trial: false, source: 'home_banner' });
    setPaywallOpen(true);
  };

  return (
    <div className='home'>
      {!isLoading || brushes.length > 0 ? (
        <>
          {!isSearchLanding && (
            <SeoIntro
              eyebrow='Procreate brush library'
              title='Best Procreate brushes and brush sets for digital artists'
              description='Explore premium Procreate brushes for sketching, tattoo flash, anime art, portraits, lettering, texture work, fashion illustration, and polished iPad drawing workflows.'
            />
          )}
          {!isSearchLanding && !hasPremium && (
            <div className='border-t-2'>
              <div className='mx-auto max-w-[1240px] w-full px-6 py-8 xs:px-4 xs:py-6'>
                <button
                  type='button'
                  className='group relative block w-full h-[320px] rounded-[12px] overflow-hidden bg-paywall-pattern bg-cover bg-center text-left cursor-pointer md:h-[240px] xs:h-[190px]'
                  onClick={handleBannerClick}
                  aria-label='Open premium access'
                >
                  <video
                    className='absolute inset-0 block w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]'
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload='metadata'
                    poster={paywallBg}
                  >
                    <source src={homeBannerVideo} type='video/quicktime' />
                  </video>
                  <span
                    className='absolute inset-0'
                    style={{
                      background:
                        'linear-gradient(90deg, rgba(0, 0, 0, 0.74) 0%, rgba(0, 0, 0, 0.34) 44%, rgba(0, 0, 0, 0.08) 100%)',
                    }}
                  />
                  <span className='relative z-[1] flex h-full max-w-[520px] flex-col justify-end p-8 text-white md:max-w-[420px] md:p-6 xs:p-5'>
                    <span className='mb-3 inline-flex w-fit items-center rounded-full bg-white/20 px-3 py-1 text-xxs leading-xxs font-bold backdrop-blur-sm'>
                      Premium
                    </span>
                    <span className='text-4xl leading-4xl font-bold md:text-3xl md:leading-3xl xs:text-2xl xs:leading-2xl'>
                      Unlock every Procreate brush
                    </span>
                    <span className='mt-3 text-base leading-base text-white/90 md:text-sm md:leading-sm xs:hidden'>
                      Get unlimited access to 1,050+ tools with weekly updates.
                    </span>
                    <span className='mt-5 inline-flex w-fit items-center gap-2 rounded-[12px] bg-white px-5 py-3 text-sm leading-sm font-bold text-main-blue md:mt-4 md:px-4 md:py-2'>
                      Unlock premium
                      <ArrowRight width={20} height={20} />
                    </span>
                  </span>
                </button>
              </div>
            </div>
          )}
          {!isSearchLanding && newBrushes.length > 0 && (
            <div className='border-t-2'>
              <div className='overflow-hidden px-6 py-8 xs:px-4 xs:py-6'>
                <div className='max-w-[1240px] mx-auto w-full'>
                  <div className='mx-auto pb-4'>
                    <h2 className='text-lg leading-xl font-bold'>For you</h2>
                  </div>
                  <Swiper
                    modules={[FreeMode]}
                    slidesPerView='auto'
                    spaceBetween={12}
                    centerInsufficientSlides
                    breakpoints={{ 480: { spaceBetween: 20 } }}
                    className='overflow-visible'
                  >
                    {newBrushes.map(({ fields, sys }) => (
                      <SwiperSlide className='w-[220px] sm:w-[180px] xs:w-[170px]' key={sys.id}>
                        <ToolCard size='small' set={fields} isBrushset category='Brushes' isFeatured />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          )}
          <div className='border-t-2 w-auto'>
            <div className={`mx-auto max-w-[1240px] w-full px-6 xs:px-4 ${isSearchLanding ? 'py-4 xs:py-3' : 'py-6 xs:py-4'}`}>
              <div className='mb-4 flex items-center justify-between gap-4 md:flex-col md:items-stretch'>
                <h2 className='shrink-0 text-lg leading-xl font-bold'>
                  {searchTerm ? `Brushes for "${searchTerm}"` : 'All brushes'}
                </h2>
                <div className='w-full max-w-[420px] md:max-w-none'>
                  <SearchBar
                    value={searchTerm}
                    placeholder='Search brushes'
                    onSearch={handleSearch}
                  />
                </div>
              </div>
              <div className='relative'>
                {brushes.length > 0 ? (
                  <InfiniteScroll
                    dataLength={brushes.length}
                    next={getBrushes}
                    // eslint-disable-next-line react/jsx-boolean-value
                    hasMore={isLoggedIn && brushes.length < total}
                    scrollableTarget='app'
                    style={{ overflow: 'visible' }}
                    loader={
                      <div className='mt-4 flex justify-center'>
                        <Spinner theme='light' />
                      </div>
                    }
                  >
                    <ul className='grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-5 xs:grid-cols-2 xs:gap-3 xxs:grid-cols-1'>
                      {brushes
                        .slice(0, isLoggedIn ? brushes.length : LOGGED_OUT_VISIBLE_BRUSHES)
                        .map(({ fields, sys }) => (
                          <li key={sys.id}>
                            <ToolCard size='large' set={fields} isBrushset category='Brushes' />
                          </li>
                        ))}
                    </ul>
                  </InfiniteScroll>
                ) : (
                  <div className='rounded-lg bg-main-grey-200 px-4 py-8 text-center text-sm leading-sm text-main-grey-800'>
                    No brushes found.
                  </div>
                )}
                {isBrushFeedLocked && (
                  <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 flex min-h-[220px] items-end justify-center bg-gradient-to-t from-white via-white/95 to-white/0 px-4 pb-2 xs:min-h-[180px]'>
                    <button
                      type='button'
                      className='pointer-events-auto rounded-[12px] bg-main-blue px-6 py-3 text-sm leading-sm font-bold text-white shadow-[0_12px_36px_rgba(25,66,150,0.22)]'
                      onClick={() => {
                        analytics.track("Auth Required", {
                          source: "locked_home_feed",
                          visible_count: LOGGED_OUT_VISIBLE_BRUSHES,
                          total_count: total,
                        });
                        setAuthOpen(true);
                      }}
                    >
                      Please log in to see more
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PaywallModal
            isOpen={isPaywallOpen}
            onClose={() => setPaywallOpen(false)}
            userId={userId}
            email={email}
            hasPremium={hasPremium}
            isTrial={(plan && plan.isTrial) || false}
          />
          <SignIn
            isOpen={isAuthOpen}
            onClose={() => setAuthOpen(false)}
            onSuccess={() => setAuthOpen(false)}
          />
        </>
      ) : (
        <SkeletonPage showSwiper={!isSearchLanding} title='brushes' />
      )}
    </div>
  );
};

export { Home };
